import { modelData } from 'app/lib/modelData'
/* oxlint-disable react/static-components -- these render APIs intentionally receive component references */
import { graphql } from 'app/generated/gql'
import * as Collapsible from '@radix-ui/react-collapsible'
import { UserCircleIcon } from '@heroicons/react/24/solid'
import classNames from 'classnames'
import {
  EditPencil,
  Lock as LockIcon,
  Phone as PhoneIcon,
  PhoneIncome as PhoneIncomeIcon,
  PhoneXmark,
  WarningHexagon as WarningHexagonIcon,
} from 'iconoir-react'
import { compact, isEmpty, startCase, uniq } from 'lodash'
import moment from 'moment'
import { Fragment, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import srtParser2 from 'srt-parser-2'
import { CallDataCollection } from './CallDataCollection'
import { CallEndedByDivider } from './CallEndedByDivider'
import { CALL_ENDED_BY_LABEL, callEndedBy } from './callEndedBy'
import { EditAIDataModal } from './EditAIDataModal'
import { StaffAssign } from './StaffAssign'
import { ActivityCard } from 'app/components/activities/ActivityCard'
import { ActivityTags } from 'app/components/activities/ActivityTags'
import { KeyValueDrawer } from 'app/components/activities/KeyValueDrawer'
import { MatchActivityModal } from 'app/components/activities/MatchActivityModal'
import { PersonName } from 'app/components/activities/PersonName'
import { PersonOutcomes } from 'app/components/activities/PersonOutcomes'
import AudioPlayer from 'app/components/AudioPlayer'
import Button from 'app/components/Button'
import { AgentToolCallRow } from 'app/components/activities/AgentToolCallRow'
import { readToolCalls } from 'app/components/ai-agent-voice-ops/callDataDebug'
import { ChevronToggle } from 'app/components/ChevronToggle'
import { useConfirmation } from 'app/components/ConfirmationContext'
import TrackableIcon from 'app/components/Icon/TrackableIcon'
import { CopyToClipboard } from 'app/components/shared/CopyToClipboard'
import { FeedDivider } from 'app/components/shared/styled/FeedDivider'
import Spinner from 'app/components/Spinner'
import { Tooltip } from 'app/components/Tooltip'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { CALL_TRANSCRIPTION_QUERY } from 'app/graphql/callTranscription'
import { useQuery } from 'urql'
import type { ActivityJson } from 'app/types/entities'
import type { CallJson, PersonSummary } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser, useWorkspace } from 'app/utils'
import {
  findActiveTranscriptLineIndex,
  formatSeconds,
  formatSpeaker,
  getSpeakerColor,
  placeToolCalls,
  processTranscriptWithTransfers,
  SPEAKER_PREFIX_PATTERN,
  stripSpeakerPrefix,
  updateTranscriptHighlight,
} from 'app/utils/transcript'
import { assertBoom } from 'lib/assertBoom'
import { useShowUtmFields } from 'app/hooks/useShowUtmFields'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import { debug, logError, trace } from 'lib/logging'
import { sortTags } from 'lib/sortTags'
import { useCallStore, useAuthStore } from 'app/stores/StoreContext'
import { REASSIGN_ACTIVITY_MUTATION } from 'app/stores/ActivityStore'
import { gqlMutation } from 'app/lib/graphqlClient'

// Exported for the response queue's record skeleton, which runs this same
// document as a gate so the two share one request and one cache entry.
export const CALL_QUERY = graphql(`
  query InboundCall($id: ID!) {
    call(id: $id) {
      ai {
        actionItems
        answerer
        channel
        extractionDate
        model
        people {
          appointmentDate
          appointmentRequested
          appointmentScheduled
          appointmentTime
          birthDate
          email
          index
          insuranceProvider
          isCaller
          name
          outcomes
          patientType
          phone
          reasonForNotScheduling
          referral
          relationship
          serviceRequested
          treatment
        }
        summary
        tokenUsage {
          inputTokens
          outputTokens
          totalTokens
        }
      }
      aiAgent
      allPatientOutcomes
      answered
      answerer
      appointmentDateString
      archived
      callerId
      callerPhone
      channel
      duration
      elevenLabsConversationId
      gclid
      id
      landingPageUrl
      locationId
      note
      patientNames
      recordingDuration
      referringUrl
      staff {
        id
        name
      }
      status
      tags
      trackingNumber
      trackingNumberName
      treatment
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      vendor
      waveforms
      workspaceId
    }
  }
`)

const CALL_RECORDING_URL_QUERY = graphql(`
  query CallRecordingUrl($id: ID!) {
    callRecordingUrl(id: $id)
  }
`)

const CALL_DATUM_QUERY = graphql(`
  query CallDatum($id: ID!) {
    callDatum(id: $id) {
      id
      callId
      elevenLabsConversationId
      extractedData
      data
    }
  }
`)

type CallDataType =
  | `Call Details`
  | `Details`
  | `Transcript`
  | `Summary`
  | `Post Processing`
  | `ElevenLabs`
  | `Data Collection`

type Props = {
  activity: ActivityJson
  contact?: ContactJson
  currentAudioPlaying?: number | string
  alwaysOpen?: boolean
  isFirst?: boolean
  isLast?: boolean
  /** See `ActivityCard`. */
  hideHeader?: boolean
  singleActivity?: boolean
  onAudioPlay?: (id: number | string) => void
  onActivityUpdated?: (activity: ActivityJson) => void
  onContactRefresh?: () => void
}
/**
 * Renders inbound Call activity record.
 * @Prop activity - Activity to query the call from.
 * @Prop contact - Contact to whom the activity belongs.
 * @Prop currentAudioPlaying - Id of the audio currently playing. Used to pause other audios.
 * @Prop alwaysOpen. Boolean. If true it hides drawer button and shows the content.
 * @Prop isFirst. Boolean. If true it hides the first activity union line.
 * @Prop isLast. Boolean. If true it hides the last activity union line.
 * @Prop onAudioPlay. Function. Callback to handle audio play.
 * @returns ReactElement
 */
export function InboundCallCard({
  activity,
  contact,
  currentAudioPlaying,
  alwaysOpen,
  isFirst,
  isLast,
  hideHeader,
  singleActivity,
  onAudioPlay,
  onActivityUpdated,
  onContactRefresh,
}: Props): ReactElement {
  trace(`activityTags`, `contact`, contact)
  const me = useUser()
  const isOrgUser = useIsOrgUser()
  const [confirm] = useConfirmation()
  const workspace = useWorkspace()
  const { products } = useAuthStore()
  const callStore = useCallStore()
  const [staff, setStaff] = useState<ActivityJson[`owner`]>(activity.owner)
  const [archiving, setArchiving] = useState(false)
  const [selectedTab, setSelectedTab] = useState<CallDataType>(`Details`)
  const [localActivity, setLocalActivity] = useState<ActivityJson>(activity)
  // TODO: TRK-1302 update to show multiple contacts
  assertBoom(activity.type === `Call`)
  const showUtmFields = useShowUtmFields()
  const agentOnly = products?.includes(`AI Agent`) && !products?.includes(`CRM`)
  const isActivityOlderThanTwoYears = useMemo(
    () => moment().diff(moment(activity.activityDate), `months`) > 24,
    [activity],
  )
  const queryContext = useMemo(() => ({ suppressErrorToast: true }), [])
  // runs on first load to grab call data
  // TODO: this should ideally only run for calls visible on screen.
  const [{ data: callResponse, error: responseError, fetching: callFetching }, refetchCall] = useQuery({
    query: CALL_QUERY,
    variables: { id: activity.typeId! },
    pause: !activity.typeId,
    context: queryContext,
    // Cache-first would serve a cached *error* forever. One request caught
    // during an expired session poisons the row for the rest of the session,
    // and every later open replays the failure without asking the API again.
    // Revalidating on mount heals that on its own. The cached record still
    // renders immediately, so nothing waits on the round trip.
    requestPolicy: `cache-and-network`,
  })

  const [{ data: callDatumResponse }] = useQuery({
    query: CALL_DATUM_QUERY,
    variables: { id: activity.typeId! },
    // Only calls the agent actually handled have a datum. Asking for every
    // other call returned "Not found" on nearly all of them, one failed request
    // per call card, for a tab that only renders when the datum exists. The
    // conversation id is the practice's own answer to whether there is one.
    // TRK-5741: fetch for all users (not just super) so the "Ended By" row and
    // transcript divider can show who ended the call.
    pause: !activity.typeId || !callResponse?.call?.elevenLabsConversationId,
    // A call can carry a conversation id with no datum yet (or ever); the
    // "Not found" is expected there and must not toast (TRK-5885).
    context: queryContext,
  })

  const [{ data: recordingData }] = useQuery({
    query: CALL_RECORDING_URL_QUERY,
    variables: { id: activity.typeId! },
    pause: !activity.typeId || isActivityOlderThanTwoYears,
    context: queryContext,
  })

  const [{ data: transcriptionData }] = useQuery({
    query: CALL_TRANSCRIPTION_QUERY,
    variables: { id: activity.typeId! },
    pause: !activity.typeId,
  })

  var parser = new srtParser2()

  // Local activity is independently updated by staff and activity actions.
  // oxlint-disable-next-line react/no-deriving-state-in-effects
  // Sync localActivity when activity prop changes
  useEffect(() => {
    // Intentional synchronization: staff and activity actions also update localActivity.
    // oxlint-disable-next-line react/no-deriving-state-in-effects
    setLocalActivity(activity)
  }, [activity])

  const isLoading = useMemo(() => callFetching && !callResponse?.call, [callFetching, callResponse])
  const transcriptionsRef = useRef<HTMLDivElement>(null)

  const onStaffChange = async (staff: ActivityJson[`owner`]) => {
    setStaff(staff)

    // API expects flat ownerId/ownerName fields, not a nested owner object
    const ownerId = staff?.id ?? `-1`
    const ownerName = staff?.name ?? `-`
    const result = await gqlMutation(REASSIGN_ACTIVITY_MUTATION, {
      id: activity.id!,
      input: { ownerId, ownerName },
    })
    if (result.error) {
      logError(`Failed to update staff`, result.error)
      notifyError(`Failed to update staff`)
      setStaff(activity.owner)
      return
    }
    const updated = result.data?.reassignActivity
      ? { ...modelData(result.data.reassignActivity), data: activity.data }
      : undefined
    // Only call activity update if it comes from ActivityDrawer
    singleActivity && updated && onActivityUpdated?.(updated)
    notifySuccess(`Staff updated successfully`)
  }

  const showMatchingModal = () => {
    showModal(<MatchActivityModal call={call} activity={activity} />, {
      title: `Manage Activity Link`,
      isFullScreen: false,
      is80Percent: true,
      removePadding: true,
      useRadix: true,
    })
  }

  const showEditAIModal = () => {
    if (!call) return
    showModal(<EditAIDataModal call={call} />, {
      title: `Edit AI Data`,
      isFullScreen: false,
      removePadding: true,
      useRadix: true,
    })
  }

  const handleArchive = async () => {
    if (!call?.id) return
    await confirm({
      title: `Archive Call`,
      description: `Are you sure you want to archive this call?`,
      variant: `danger`,
    }).then(async () => {
      setArchiving(true)
      try {
        const result = await callStore.remove(call.id!)
        if (!result.error) {
          notifySuccess(`Call archived`)
          onContactRefresh?.()
        } else {
          notifyError(`Failed to archive call`)
        }
      } finally {
        setArchiving(false)
      }
    })
  }

  // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  const call: CallJson | undefined = useMemo(() => callResponse?.call as CallJson | undefined, [callResponse])
  const callData = useMemo(() => callDatumResponse?.callDatum, [callDatumResponse])
  // The API answers null when the call has no audio in S3 (TRK-5885). Say so instead of drawing an
  // idle player whose play button can never work.
  const hasNoRecording = recordingData?.callRecordingUrl === null
  const terminationReason = useMemo(
    () =>
      (
        callData?.data as {
          transcriptWebhook?: { metadata?: { termination_reason?: string | null } }
        }
      )?.transcriptWebhook?.metadata?.termination_reason,
    [callData],
  )
  const endedBy = callEndedBy(terminationReason)
  const transcription = useMemo(
    () => (transcriptionData?.callTranscription ? parser.fromSrt(transcriptionData.callTranscription) : undefined),
    [transcriptionData],
  )
  const processedTranscript = useMemo(
    () => (transcription ? processTranscriptWithTransfers(transcription) : undefined),
    [transcription],
  )

  const diarized = useMemo(
    () => processedTranscript?.lines.some((line) => SPEAKER_PREFIX_PATTERN.test(line.text)) ?? false,
    [processedTranscript],
  )
  // TRK-5934: super admins see each agent tool call between the transcript lines where it happened.
  // An AI call's transcript carries ElevenLabs' own timings, so the two line up to the second.
  const isSuper = me?.type === `super`
  const toolCallsByLine = useMemo(
    () =>
      isSuper && processedTranscript
        ? placeToolCalls(processedTranscript.lines, processedTranscript.transferIndex, readToolCalls(callData?.data))
        : undefined,
    [isSuper, processedTranscript, callData],
  )

  function handlePlayerTimeUpdate(currentTime: number) {
    if (!processedTranscript) return
    const activeLineIndex = findActiveTranscriptLineIndex(
      processedTranscript.lines,
      currentTime,
      processedTranscript.transferIndex,
      processedTranscript.timeOffset,
    )
    updateTranscriptHighlight(transcriptionsRef, activeLineIndex)
  }
  const ai: CallJson[`ai`] = call?.ai
  const patientOutcomes: string[] | undefined = call?.allPatientOutcomes
  const [seekTo, setSeekTo] = useState<number | undefined>(undefined)

  // Reset seekTo when the activity changes so stale timestamps don't auto-play the new call
  useEffect(() => {
    setSeekTo(undefined)
  }, [activity.id])

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const Icon = () => {
    const iconProps = { className: `h-5 w-5 rounded-md bg-blue-600 text-on-color p-1` }

    if (activity?.tags?.includes(`Missed Call`) || call?.tags?.includes(`Voicemail`)) {
      return <PhoneXmark {...iconProps} />
    } else if (call?.answered) {
      return <PhoneIncomeIcon {...iconProps} />
    } else return <PhoneIcon {...iconProps} />
  }

  const getCallTitle = () => {
    return `Inbound Call`
  }

  const activityTags = useCallback(() => {
    let activityTags: string[] | undefined = []
    let peopleIndex = localActivity.contacts?.find((c) => c.id === contact?.id)?.peopleIndex
    let selectedContact: PersonSummary | undefined = peopleIndex != null ? ai?.people?.[peopleIndex] : undefined
    debug(`activityTags`, `selectedContact`, contact, selectedContact)
    activityTags = selectedContact
      ? selectedContact.outcomes
      : compact(patientOutcomes?.length ? patientOutcomes : localActivity?.tags).flatMap((tag) => tag.split(`,`))
    if (localActivity?.tags?.includes(`AI`)) {
      activityTags = [`AI`, ...(activityTags || [])]
    }
    activityTags = uniq([...(activityTags || [])])
    // Agent preview (test) calls are archived but visible to super users.
    // "Archived" is injected here (not persisted as a tag) so super users can tell it's a test call.
    if (localActivity?.archived) {
      activityTags = [`Archived`, ...activityTags]
    }
    let sorted = sortTags(activityTags)
    return <ActivityTags activityTags={sorted} workspaceId={localActivity.workspaceId} isLoading={isLoading} />
  }, [call, localActivity])

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const Tabs = () => {
    return (
      <div className="flex gap-x-6 pt-3" aria-label="Tabs">
        {compact([
          ai || activity.needsReview ? `Summary` : `Details`,
          `Transcript`,
          ai || activity.needsReview ? `Call Details` : undefined,
          callData?.data ? `ElevenLabs` : undefined,
          me?.type === `super` && (ai || callData?.data) ? `Data Collection` : undefined,
        ]).map((tab) => (
          <button
            key={tab}
            className={classNames(
              tab === selectedTab
                ? `text-strong border-blue-500`
                : `border-transparent text-ui-xs text-subtle hover:border-transparent hover:text-subtle`,
              `flex items-center gap-x-2 whitespace-nowrap border-b-[2px] pb-2.5 text-ui-xs`,
              !ai && !activity.needsReview && tab === `Transcript` && `cursor-auto`,
            )}
            onClick={() =>
              setSelectedTab((prev) => {
                if (!(ai && transcription) && !activity.needsReview && tab === `Transcript`) {
                  return prev
                }
                return tab as CallDataType
              })
            }
          >
            <span>{tab}</span>
            {!(ai && transcription) && !activity.needsReview && tab === `Transcript` && <LockIcon />}
          </button>
        ))}
      </div>
    )
  }

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const LinkedContact = ({
    contacts,
    person,
  }: {
    contacts: ActivityJson[`contacts`]
    person?: PersonSummary
  }): ReactElement | null => {
    if (!person && contacts?.length) {
      return (
        <span className="mt-0.5 w-fit break-all rounded border bg-surface-subtle px-1.5 text-ui-xs capitalize leading-4 text-strong">
          {contacts[0].name?.toLocaleLowerCase()}
        </span>
      )
    }
    const contact = contacts?.find((contact) => contact.name === person?.name)
    if (isEmpty(contact)) return null
    return (
      <a
        className="mt-0.5 w-fit break-all rounded border bg-surface-subtle px-1.5 text-ui-xs capitalize leading-4 text-strong"
        href={`/contacts?drawer=${contact.id}&wId=${workspace?.id || null}`}
        target="_blank"
        rel="noreferrer"
      >
        {contact.name}
      </a>
    )
  }
  const getNeedsReview = () => {
    debug([`activity`, `call`], `needs review`, JSON.stringify(activity.reviewReason))
    return (
      <>
        <div className="mt-3 flex flex-col border-y border-default px-4">
          <Tabs />
        </div>
        <div className="px-4 pb-1 pt-4">
          {selectedTab === `Call Details` ? (
            <>
              <KeyValueDrawer
                map={{
                  Staff: (
                    <StaffAssign
                      staff={staff}
                      workspaceId={call?.workspaceId}
                      locationId={call?.locationId}
                      onChange={(staff) => onStaffChange(staff)}
                    />
                  ),
                  Channel: call?.channel || activity?.channel || `-`,
                  'Caller Phone': formatPhoneNumber(call?.callerPhone) || `-`,
                  'Caller ID': startCase(call?.callerId) || `-`,
                  'Tracking Number': formatPhoneNumber(call?.trackingNumber) || `-`,
                  'Tracking Number Name': startCase(call?.trackingNumberName) || `-`,
                  ...(showUtmFields && { Source: call?.utmSource || `-` }),
                  ...(showUtmFields && { Medium: call?.utmMedium || `-` }),
                  ...(showUtmFields && { Campaign: call?.utmCampaign || `-` }),
                  Keyword: call?.utmKeywords || `-`,
                  ...(showUtmFields && { Content: call?.utmContent || `-` }),
                  ...(showUtmFields && { GCLID: call?.gclid || `-` }),
                  ...(showUtmFields && { 'Referral URL': call?.referringUrl || `-` }),
                }}
                className={{
                  Source: `lowercase`,
                  Medium: `lowercase`,
                  Keyword: `lowercase`,
                  Content: `lowercase`,
                  Campaign: `lowercase`,
                  'Referral URL': `lowercase`,
                  'Caller Phone': `phi`,
                }}
                omitLabels={[`Staff`]}
              />
              {me?.type === `super` && (
                <div className="mt-3 flex w-full flex-col text-body-xs text-strong">
                  <span className="text-subtle">Call ID</span>
                  <div className="mt-0.5 flex items-center gap-x-1 text-ui-xs text-strong">
                    {call?.id || `-`}
                    <CopyToClipboard text={call?.id || `-`} />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex h-64 w-full grow items-center justify-center rounded border-[0.5px] bg-red-100 align-middle">
              <div className="text-center text-body-xs text-red-800">
                <WarningHexagonIcon height={24} width={24} className="mb-1 w-full grow self-end text-red-800" />
                {activity.description || activity.reviewReason}
              </div>
            </div>
          )}
        </div>
      </>
    )
  }
  useEffect(() => {
    if ((ai || activity.needsReview) && selectedTab !== `Call Details`) {
      setSelectedTab(`Summary`)
    }
  }, [ai, activity])

  if (!me) return <></>

  return (
    <ActivityCard
      hideHeader={hideHeader}
      icon={<Icon />}
      title={<span className="text-ui-sm text-strong">{getCallTitle()}</span>}
      activityDate={localActivity?.activityDate}
      tags={activityTags()}
      content={
        <div id="activity-record-call" className="block w-full px-4">
          {hasNoRecording ? (
            <p className="text-ui-xs text-subtle">No recording is available for this call.</p>
          ) : (
            <AudioPlayer
              key={activity.id}
              id={activity.id!}
              callId={call?.id}
              className="p-0"
              imageHeight="h-7"
              currentAudioPlaying={currentAudioPlaying}
              waveforms={call?.waveforms}
              url={isActivityOlderThanTwoYears ? undefined : (recordingData?.callRecordingUrl ?? undefined)}
              urlNotFoundMessage={
                responseError
                  ? `There was an error loading this call.`
                  : isActivityOlderThanTwoYears
                    ? `There was an error loading this call's audio. It may be too old.`
                    : undefined
              }
              onPlay={(id) => {
                onAudioPlay?.(id!)
              }}
              seekTo={seekTo}
              onTimeUpdate={handlePlayerTimeUpdate}
            />
          )}
        </div>
      }
      drawer={
        !ai && (
          <>
            {isLoading ? (
              <div className="mt-3 flex items-center justify-center border-t border-default pt-3">
                <Spinner size={4} />
              </div>
            ) : responseError && !call && !callFetching ? (
              // A failed fetch used to fall through to the tabs below, which
              // draw the same empty Details/Transcript shell a call with no AI
              // extraction draws. The two are not the same thing, and reading
              // an outage as "nothing was recorded on this call" is the worse
              // of the two mistakes: it looks like an answer.
              <div className="mt-3 flex items-center justify-between gap-3 border-y border-default px-4 py-3">
                <span className="text-body-xs text-subtle">This call could not be loaded.</span>
                <Button
                  label="Try again"
                  mode="white"
                  size="sm"
                  type="button"
                  className="h-6 !px-3 !text-ui-xs"
                  onClick={() => refetchCall({ requestPolicy: `network-only` })}
                />
              </div>
            ) : (
              <>
                <div className="mt-3 flex justify-between border-y border-default px-4">
                  <Tabs />
                  <div className="flex items-center gap-2 self-center">
                    {me?.type === `super` && !isLoading && (
                      <Button
                        label="Archive"
                        mode="white"
                        size="sm"
                        type="button"
                        className="h-6 !border-amber-500/20 !bg-amber-500/20 !px-3 !text-ui-xs !text-amber-800"
                        onClick={handleArchive}
                        loading={archiving}
                        disabled={!call}
                      />
                    )}
                    {!isLoading && (
                      <Button
                        label="Manage"
                        mode="white"
                        type="button"
                        className="!text-trn-black h-6 !px-3 !text-ui-xs"
                        onClick={showMatchingModal}
                        disabled={!call}
                      />
                    )}
                  </div>
                </div>
                <div className="mt-3 px-4 pt-3">
                  {selectedTab === `Data Collection` ? (
                    <CallDataCollection callData={callData} />
                  ) : products?.includes(`Call Processing`) &&
                    call &&
                    call.recordingDuration &&
                    (!call.tags || call.tags?.includes(`In progress`)) ? (
                    <div className="w-full rounded-lg bg-surface-muted p-4">
                      <div className="flex items-center gap-3">
                        <WarningHexagonIcon height={12} width={12} className="h-3 w-3 text-subtle" />
                        <p className="text-label-xs text-default">Call Analysis in Progress</p>
                      </div>
                      <p className="mt-2 text-caption-md text-subtle">
                        This call recording is being transcribed and analyzed by AI to extract key insights. Please wait
                        while the process is completed. The information will appear here once it's ready.
                      </p>
                    </div>
                  ) : call ? (
                    <>
                      <KeyValueDrawer
                        map={{
                          'Phone Number': formatPhoneNumber(call.callerPhone),
                          'Caller ID': call.callerId || `-`,
                          'Tracking Number': formatPhoneNumber(call.trackingNumber) || `-`,
                          'Tracking Number Name': startCase(call.trackingNumberName) || `-`,
                          Duration: call.duration || `-`,
                          Channel: call.channel || `-`,
                          Staff: (
                            <StaffAssign
                              staff={staff}
                              workspaceId={call.workspaceId}
                              locationId={call.locationId}
                              onChange={(staff) => onStaffChange(staff)}
                            />
                          ),
                          Reason: call.treatment || `-`,
                          Name: call.patientNames || call?.note?.includes(`No name`) ? `No name` : `-`,
                          'Appointment Date': formatDate({ value: call.appointmentDateString }) || `-`,
                          // 'Linked Contact':
                          //   compact(activity.contacts).length > 0
                          //     ? LinkedContact({ contacts: activity.contacts! })
                          //     : undefined,
                          ...(showUtmFields && { Source: call?.utmSource || `-` }),
                          ...(showUtmFields && { Medium: call?.utmMedium || `-` }),
                          ...(showUtmFields && { Campaign: call?.utmCampaign || `-` }),
                          Keyword: call?.utmKeywords || `-`,
                          ...(showUtmFields && { Content: call?.utmContent || `-` }),
                          ...(showUtmFields && { GCLID: call?.gclid || `-` }),
                          ...(showUtmFields && { 'Referral URL': call?.referringUrl || `-` }),
                        }}
                        className={{
                          'Caller ID': `lowercase`,
                          Source: `lowercase`,
                          Medium: `lowercase`,
                          Keyword: `lowercase`,
                          Content: `lowercase`,
                          Campaign: `lowercase`,
                          'Referral URL': `lowercase`,
                        }}
                        omitLabels={[`Staff`]}
                      />
                      {me?.type === `super` && (
                        <div className="mt-3 flex w-full flex-col text-body-xs text-strong">
                          <span className="text-subtle">Call ID</span>
                          <div className="mt-0.5 flex items-center gap-x-1 text-ui-xs text-strong">
                            {call?.id || `-`}
                            <CopyToClipboard text={call?.id || `-`} />
                          </div>
                        </div>
                      )}
                      {showUtmFields && (
                        <div className="mt-3 flex w-full flex-col text-body-xs text-strong">
                          <span className="text-subtle">Landing Page URL</span>
                          <div className="mt-0.5 flex items-center gap-x-1 break-all text-ui-xs text-strong">
                            {call?.landingPageUrl || `-`}
                            {call?.landingPageUrl && <CopyToClipboard text={call?.landingPageUrl || `-`} />}
                          </div>
                        </div>
                      )}
                      <div className="mt-3 flex w-full flex-col text-body-xs text-strong">
                        <span className="text-subtle">Note</span>
                        <div className="mt-0.5 text-ui-xs text-strong">{call?.note || `-`}</div>
                      </div>
                    </>
                  ) : null}
                </div>
              </>
            )}
          </>
        )
      }
      aiContent={
        ai ? (
          <>
            <div className="mt-3 flex items-center justify-between border-y border-default px-4">
              {isLoading ? <Spinner size={4} /> : <Tabs />}
              {/* {!!call?.aiAgent && (
                <Button
                  label="Report a Problem"
                  mode="white"
                  type="button"
                  id="report-problem"
                  className="!text-trn-black h-6 !px-3 !text-ui-xs"
                />
              )} */}
            </div>
            {(selectedTab === `Summary` || selectedTab === `Post Processing`) && (
              <div className="flex flex-col gap-y-3 px-4">
                <div className="mt-3 flex w-full flex-col gap-y-1.5">
                  <span className="text-body-xs text-subtle">Summary</span>
                  <span className="text-body-xs text-strong">{ai.summary || `-`}</span>
                </div>
                <div className="mt-3 flex w-full flex-col gap-y-1.5">
                  <span className="text-body-xs text-subtle">Action Items</span>
                  <span className="text-body-xs text-strong">{ai.actionItems || `-`}</span>
                </div>
                {me?.type === `super` && (
                  <div className="mt-3 flex w-full flex-col gap-y-1.5">
                    <span className="text-body-xs text-subtle">Extraction Date</span>
                    <span className="text-body-xs text-strong">
                      {ai.extractionDate
                        ? formatDate({
                            value: ai.extractionDate,
                            useLocalUtc: true,
                          })
                        : `-`}
                    </span>
                  </div>
                )}
                <div className="flex w-full flex-col gap-y-3">
                  <div className="flex flex-wrap justify-between gap-y-2 md:flex-nowrap md:gap-y-0">
                    <div className="flex items-center gap-x-2">
                      <span className="whitespace-nowrap text-body-xs text-subtle">People identified by</span>
                      <TrackableIcon className="h-5 w-5 rounded-md bg-inverse bg-gradient-to-br p-1 text-inverse" />
                      <span className="text-body-xs text-subtle">A.I.</span>
                    </div>
                    <div className="flex items-center gap-x-2">
                      {isOrgUser && (
                        <Tooltip message="Edit AI extraction data" placement="top">
                          <button
                            type="button"
                            onClick={showEditAIModal}
                            className="btn-tertiary size-6 flex items-center justify-center rounded-md"
                          >
                            <EditPencil width={14} height={14} className="text-default" />
                          </button>
                        </Tooltip>
                      )}
                      {me?.type === `super` && (
                        <Button
                          label="Archive"
                          mode="white"
                          size="sm"
                          type="button"
                          className="h-6 !border-amber-500/20 !bg-amber-500/20 !px-3 !text-ui-xs !text-amber-800"
                          onClick={handleArchive}
                          loading={archiving}
                          disabled={!call}
                        />
                      )}
                      {!agentOnly && (
                        <Button
                          label="Manage"
                          mode="white"
                          type="button"
                          className="!text-trn-black h-6 !px-3 !text-ui-xs"
                          onClick={showMatchingModal}
                          disabled={!call}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-y-1">
                    {ai?.people
                      ?.map((p, i) => ({ ...p, originalIndex: i })) // keeps track of the original index before the sort takes place
                      ?.sort((person) => (person.isCaller ? -1 : 1)) // sorts by isCaller first
                      .map((person) => (
                        <Collapsible.Root
                          key={`${person.name}-${person.originalIndex}`}
                          className="group/person rounded-lg border border-default bg-surface-subtle shadow-elevation-1"
                        >
                          <Collapsible.Trigger className="flex w-full flex-col gap-y-2 px-4 py-2 data-[state=open]:border-b data-[state=open]:border-default">
                            <div className="flex w-full items-center gap-x-1">
                              <PersonName person={person} isCaller={!!(person.isCaller && ai!.people!.length > 1)} />
                              <div className="ml-auto flex shrink-0 items-center gap-x-2">
                                <ChevronToggle chevronClassName="group-data-[state=open]/person:rotate-180" />
                              </div>
                            </div>
                            <PersonOutcomes
                              activity={activity}
                              person={person}
                              personIndex={person.originalIndex}
                              workspaceId={activity.workspaceId}
                            />
                          </Collapsible.Trigger>
                          <Collapsible.Content className="overflow-hidden rounded-b-lg bg-surface px-4 py-3">
                            <KeyValueDrawer
                              map={{
                                Name: startCase(person.name || `Unknown`),
                                Phone: formatPhoneNumber(person.phone),
                                Relationship: person.relationship,
                                'Birth Date': formatDate({ value: person.birthDate, dateOnly: true }),
                                'Patient Status': person.patientType,
                                Treatment: person.treatment || person.serviceRequested,
                                'Apt. Schedule': person.appointmentScheduled ? `Yes` : `No`,
                                'Apt. Date': formatDate({ value: person.appointmentDate, dateOnly: true }),
                                Time: person.appointmentTime,
                                'Insurance Provider': person.insuranceProvider,
                                'Linked Contact':
                                  compact(ai.people).length > 0
                                    ? LinkedContact({ contacts: activity.contacts!, person })
                                    : undefined,
                              }}
                              className={{
                                Source: `lowercase`,
                                Medium: `lowercase`,
                                Keyword: `lowercase`,
                                Content: `lowercase`,
                                Campaign: `lowercase`,
                                'Referral URL': `lowercase`,
                              }}
                            />
                          </Collapsible.Content>
                        </Collapsible.Root>
                      ))}
                  </div>
                </div>
                {me?.type === `super` && callData?.data && (
                  <div className="max-w-[606px]">
                    <Collapsible.Root className="group/debug rounded border border-default bg-surface-subtle">
                      <Collapsible.Trigger className="flex w-full items-center justify-between px-4 py-2 text-left text-ui-xs text-strong">
                        <span>ElevenLabs Debug Data</span>
                        <ChevronToggle chevronClassName="group-data-[state=open]/debug:rotate-180" />
                      </Collapsible.Trigger>
                      <Collapsible.Content className="border-t border-default bg-surface px-4 py-3">
                        <div className="flex items-center justify-end">
                          <CopyToClipboard text={JSON.stringify(callData.data, null, 2)} />
                        </div>
                        <pre className="mt-2 max-h-96 overflow-auto rounded border border-default bg-surface-subtle p-3 text-[11px] leading-4 text-strong">
                          {JSON.stringify(callData.data, null, 2)}
                        </pre>
                      </Collapsible.Content>
                    </Collapsible.Root>
                  </div>
                )}
              </div>
            )}
            {selectedTab === `Transcript` && processedTranscript && (
              <div className="bg-surface-subtle">
                <div ref={transcriptionsRef} className="flex flex-col overflow-hidden bg-surface">
                  {toolCallsByLine?.get(-1)?.map((toolCall, j) => (
                    <AgentToolCallRow key={`tool-start-${j}`} toolCall={toolCall} />
                  ))}
                  {processedTranscript.lines.map((line, i) => {
                    const toolCallRows = toolCallsByLine
                      ?.get(i)
                      ?.map((toolCall, j) => <AgentToolCallRow key={`tool-${j}`} toolCall={toolCall} />)
                    return (
                      <Fragment key={i}>
                        {line.textBeforeTransfer && (
                          <div
                            data-line-index={i}
                            className={classNames(`flex cursor-pointer px-4 py-2`)}
                            onClick={() => setSeekTo(line.adjustedStart)}
                          >
                            {diarized && (
                              <UserCircleIcon
                                className="h-6 w-6 shrink-0"
                                color={getSpeakerColor(line.textBeforeTransfer)}
                              />
                            )}
                            <div className={diarized ? `ml-3` : undefined}>
                              <div className="flex items-center">
                                {diarized && (
                                  <p className="text-heading-xs leading-5 text-strong">
                                    {formatSpeaker(line.textBeforeTransfer)}
                                  </p>
                                )}
                                <p className={classNames(`text-caption-md text-subtle`, diarized && `ml-2`)}>
                                  {formatSeconds(line.adjustedStart)}
                                </p>
                              </div>
                              <p className="mt-2 text-body-sm text-strong">
                                {stripSpeakerPrefix(line.textBeforeTransfer)}
                              </p>
                            </div>
                          </div>
                        )}
                        {line.isTransferLine ? (
                          <>
                            {toolCallRows}
                            <FeedDivider text="Transferred" className="my-1.5" />
                          </>
                        ) : (
                          <div
                            data-line-index={i}
                            className={classNames(`flex cursor-pointer px-4 py-2`)}
                            onClick={() => setSeekTo(line.adjustedStart)}
                          >
                            {diarized && (
                              <UserCircleIcon className="h-6 w-6 shrink-0" color={getSpeakerColor(line.text)} />
                            )}
                            <div className={diarized ? `ml-3` : undefined}>
                              <div className="flex items-center">
                                {diarized && (
                                  <p className="text-heading-xs leading-5 text-strong">{formatSpeaker(line.text)}</p>
                                )}
                                <p className={classNames(`text-caption-md text-subtle`, diarized && `ml-2`)}>
                                  {formatSeconds(line.adjustedStart)}
                                </p>
                              </div>
                              <p className="mt-2 text-body-sm text-strong">{stripSpeakerPrefix(line.text)}</p>
                            </div>
                          </div>
                        )}
                        {!line.isTransferLine && toolCallRows}
                      </Fragment>
                    )
                  })}
                  <CallEndedByDivider terminationReason={terminationReason} className="my-1.5" />
                </div>
              </div>
            )}
            {selectedTab === `Data Collection` && (
              <div className="px-4">
                <CallDataCollection ai={ai} callData={callData} />
              </div>
            )}
            {selectedTab === `Call Details` && (
              <>
                <div className="mt-3 px-4">
                  <KeyValueDrawer
                    map={{
                      Staff: (
                        <StaffAssign
                          staff={staff}
                          workspaceId={call?.workspaceId}
                          locationId={call?.locationId}
                          onChange={(staff) => onStaffChange(staff)}
                        />
                      ),
                      Channel: call?.channel || `-`,
                      ...(endedBy && { 'Ended By': CALL_ENDED_BY_LABEL[endedBy] }),
                      'Caller Phone': formatPhoneNumber(call?.callerPhone) || `-`,
                      'Caller ID': startCase(call?.callerId) || `-`,
                      'Tracking Number': formatPhoneNumber(call?.trackingNumber) || `-`,
                      'Tracking Number Name': startCase(call?.trackingNumberName) || `-`,
                      ...(showUtmFields && { Source: call?.utmSource || `-` }),
                      ...(showUtmFields && { Medium: call?.utmMedium || `-` }),
                      ...(showUtmFields && { Campaign: call?.utmCampaign || `-` }),
                      Keyword: call?.utmKeywords || `-`,
                      ...(showUtmFields && { Content: call?.utmContent || `-` }),
                      ...(showUtmFields && { GCLID: call?.gclid || `-` }),
                      ...(showUtmFields && { 'Referral URL': call?.referringUrl || `-` }),
                      ...(me?.type === `super` && { 'GPT Channel': ai.channel || `-` }),
                      ...(me?.type === `super` && { 'AI Model': ai.model || `-` }),
                    }}
                    className={{
                      'Caller ID': `lowercase`,
                      Source: `lowercase`,
                      Medium: `lowercase`,
                      Keyword: `lowercase`,
                      Content: `lowercase`,
                      Campaign: `lowercase`,
                      'Referral URL': `lowercase`,
                      'Caller Phone': `phi`,
                    }}
                    omitLabels={[`Staff`]}
                  />
                  {me?.type === `super` && (
                    <div className="mt-3 flex w-full flex-col text-body-xs text-strong">
                      <span className="text-subtle">Call ID</span>
                      <div className="mt-0.5 flex items-center gap-x-1 text-ui-xs text-strong">
                        {call?.id || `-`}
                        <CopyToClipboard text={call?.id || `-`} />
                      </div>
                    </div>
                  )}
                  {showUtmFields && (
                    <div className="mt-3 flex w-full flex-col text-body-xs text-strong">
                      <span className="text-subtle">Landing Page URL</span>
                      <div className="mt-0.5 flex items-center gap-x-1 break-all text-ui-xs text-strong">
                        {call?.landingPageUrl || `-`}
                        {call?.landingPageUrl && <CopyToClipboard text={call?.landingPageUrl || `-`} />}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        ) : null
      }
      errorContent={activity.needsReview ? getNeedsReview() : undefined}
      alwaysOpen={alwaysOpen}
      isFirst={isFirst}
      isLast={isLast}
      singleActivity={singleActivity}
    />
  )
}
